import {
  Label,
  PolarGrid,
  PolarRadiusAxis,
  RadialBar,
  RadialBarChart,
} from 'recharts'
import { cn } from '@/lib/utils'
import {
  type ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  ChartLegend,
  ChartLegendContent,
} from '@/components/ui/chart'

/**
 * Figma (node 533:6153 — Radial Chart):
 *
 * 6 variants:
 *   Basic   — Concentric radial bars (multiple series, different colors)
 *   Label   — Radial bars with text labels on each bar
 *   Grid    — Radial bars with circular grid background
 *   Text    — Single arc with center value text (e.g. "200 Visitors")
 *   Shape   — Single half-arc (startAngle/endAngle) with center text
 *   Stacked — Multiple stacked arcs with center text
 *
 * Specs:
 *   Colors: --chart-1 through --chart-5
 *   Background sector: fill-muted
 *   Center text: text-3xl font-bold + text-xs text-muted-foreground
 *   Bar corner radius: 5
 */

export type RadialChartVariant = 'basic' | 'label' | 'grid' | 'text' | 'shape' | 'stacked'

export interface IntuiRadialChartProps {
  /** Chart data — each entry needs a value key + fill color */
  data: Record<string, unknown>[]
  /** Chart configuration */
  config: ChartConfig
  /** Data key for values */
  dataKey?: string
  /** Chart variant */
  variant?: RadialChartVariant
  /** Show chart legend */
  showLegend?: boolean
  /** Show tooltip */
  showTooltip?: boolean
  /** Center label text (for text/shape/stacked variants) */
  centerLabel?: string
  /** Center value (for text/shape/stacked variants) */
  centerValue?: string | number
  /** Start angle for the arc */
  startAngle?: number
  /** End angle for the arc */
  endAngle?: number
  /** Inner radius */
  innerRadius?: number
  /** Outer radius */
  outerRadius?: number
  /** Additional className */
  className?: string
}

function IntuiRadialChart({
  data,
  config,
  dataKey = 'visitors',
  variant = 'basic',
  showLegend = false,
  showTooltip = true,
  centerLabel,
  centerValue,
  startAngle,
  endAngle,
  innerRadius,
  outerRadius,
  className,
}: IntuiRadialChartProps) {
  const isTextVariant = variant === 'text' || variant === 'shape' || variant === 'stacked'
  const resolvedStart = startAngle ?? (variant === 'shape' ? 180 : 0)
  const resolvedEnd = endAngle ?? (variant === 'shape' ? 0 : 250)
  const resolvedInner = innerRadius ?? (isTextVariant ? 80 : 30)
  const resolvedOuter = outerRadius ?? 110

  return (
    <ChartContainer config={config} className={cn('mx-auto aspect-square h-[250px]', className)}>
      <RadialBarChart
        data={data}
        startAngle={resolvedStart}
        endAngle={resolvedEnd}
        innerRadius={resolvedInner}
        outerRadius={resolvedOuter}
      >
        {showTooltip && (
          <ChartTooltip
            cursor={false}
            content={<ChartTooltipContent hideLabel />}
          />
        )}
        {variant === 'grid' && <PolarGrid gridType="circle" />}
        {isTextVariant && (
          <PolarRadiusAxis tick={false} tickLine={false} axisLine={false}>
            {(centerValue !== undefined || centerLabel) && (
              <Label
                content={({ viewBox }) => {
                  if (viewBox && 'cx' in viewBox && 'cy' in viewBox) {
                    return (
                      <text x={viewBox.cx} y={viewBox.cy} textAnchor="middle" dominantBaseline="middle">
                        {centerValue !== undefined && (
                          <tspan x={viewBox.cx} y={viewBox.cy} className="fill-foreground text-3xl font-bold">
                            {typeof centerValue === 'number' ? centerValue.toLocaleString() : centerValue}
                          </tspan>
                        )}
                        {centerLabel && (
                          <tspan x={viewBox.cx} y={(viewBox.cy || 0) + 24} className="fill-muted-foreground text-xs">
                            {centerLabel}
                          </tspan>
                        )}
                      </text>
                    )
                  }
                }}
              />
            )}
          </PolarRadiusAxis>
        )}
        <RadialBar
          dataKey={dataKey}
          stackId={variant === 'stacked' ? 'stack' : undefined}
          cornerRadius={5}
          background
          label={variant === 'label' ? { position: 'insideStart', className: 'fill-white text-xs capitalize mix-blend-luminosity' } : undefined}
        />
        {showLegend && <ChartLegend content={<ChartLegendContent />} />}
      </RadialBarChart>
    </ChartContainer>
  )
}
IntuiRadialChart.displayName = 'IntuiRadialChart'

export { IntuiRadialChart }
export type { ChartConfig }
